import { logApiCall } from './frontendLogger';

// Store original fetch
const originalFetch = window.fetch;

// Track active requests for performance monitoring
const activeRequests = new Map<string, number>();

// Override global fetch
window.fetch = async function(...args: Parameters<typeof fetch>) {
  const [input, init] = args;
  const url = typeof input === 'string' ? input : input instanceof Request ? input.url : input.toString();
  const method = init?.method || 'GET';
  const requestId = `${method}-${url}-${Date.now()}`;
  
  // Track request start time
  activeRequests.set(requestId, Date.now());
  
  try {
    // Call original fetch
    const response = await originalFetch(...args);
    
    // Calculate duration
    const startTime = activeRequests.get(requestId) || Date.now();
    const duration = Date.now() - startTime;
    activeRequests.delete(requestId);
    
    // Clone response to read status
    const clonedResponse = response.clone();
    
    // Log API call
    logApiCall(method, url, response.status, duration);
    
    // Log error details for failed requests
    if (!response.ok) {
      try {
        const errorData = await clonedResponse.json();
        logApiCall(method, url, response.status, duration, errorData);
      } catch {
        // Ignore JSON parse errors
      }
    }
    
    return response;
  } catch (error) {
    // Calculate duration for failed requests
    const startTime = activeRequests.get(requestId) || Date.now();
    const duration = Date.now() - startTime;
    activeRequests.delete(requestId);
    
    // Log network errors
    logApiCall(method, url, 0, duration, error);
    
    throw error;
  }
};

// Setup axios interceptor if axios is used
export function setupAxiosInterceptor(axios: any) {
  // Request interceptor
  axios.interceptors.request.use(
    (config: any) => {
      config.metadata = { startTime: Date.now() };
      return config;
    },
    (error: any) => {
      logApiCall(
        error.config?.method || 'UNKNOWN',
        error.config?.url || 'UNKNOWN',
        0,
        0,
        error
      );
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axios.interceptors.response.use(
    (response: any) => {
      const duration = Date.now() - (response.config.metadata?.startTime || Date.now());
      logApiCall(
        response.config.method,
        response.config.url,
        response.status,
        duration
      );
      return response;
    },
    (error: any) => {
      const duration = Date.now() - (error.config?.metadata?.startTime || Date.now());
      logApiCall(
        error.config?.method || 'UNKNOWN',
        error.config?.url || 'UNKNOWN',
        error.response?.status || 0,
        duration,
        error
      );
      return Promise.reject(error);
    }
  );
}

// Export function to get active request count
export function getActiveRequestCount(): number {
  return activeRequests.size;
}

// Clean up old requests periodically
setInterval(() => {
  const now = Date.now();
  const timeout = 5 * 60 * 1000; // 5 minutes
  
  for (const [requestId, startTime] of Array.from(activeRequests.entries())) {
    if (now - startTime > timeout) {
      activeRequests.delete(requestId);
    }
  }
}, 60000); // Check every minute